/* eslint-disable react-hooks/rules-of-hooks */
import { AvatarDropdown, AvatarName, Footer, Question, SelectLang } from '@/components';
// import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings, MenuDataItem } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig, RequestConfig } from '@umijs/max';
import { history, Link, useLocation } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { getFirestore, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { firebaseApp } from '@/utils/firebase';
import { PRIMARY_COLOR } from '@/utils/color';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
// import { flushSync } from 'react-dom';
// import { UserManagement } from './components/RightContent';
import ForbiddenPage from './pages/403';
import GameMenuHeader from '@/components/GameMenuHeader';
import { getGame, updateGame } from '@/services/game';
import { Role } from '@/utils/constants';
import { Space } from 'antd';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  currentGame?: API.GameApplication;
  loading?: boolean;
  fetchUser?: () => Promise<API.CurrentUser | undefined>;
  fetchGame?: (gameId: string) => Promise<API.GameApplication | undefined>;
  // updateUser?: () => Promise<API.CurrentUser | undefined>;
  updateGame?: (gameId: string, data: any) => Promise<any>;
}> {
  const auth = getAuth(firebaseApp);

  // Lấy gameId từ URL hiện tại
  const gamePath = window.location.pathname.split('/')[1];
  const gameId = gamePath === 'games' ? window.location.pathname.split('/')[2] : null;

  // Khôi phục game data từ localStorage dựa vào gameId
  let currentGame;

  await new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });

  const fetchUser = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      return undefined;
    }

    // Kiểm tra role trong localStorage
    localStorage.removeItem('userRole');
    const cachedRole = localStorage.getItem('userRole');

    if (cachedRole) {
      // Nếu có role trong cache, dùng luôn
      return {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        role: cachedRole,
      } as API.CurrentUser;
    }

    // Nếu không có trong cache, query từ Firestore
    const db = getFirestore(firebaseApp);
    const userDocRef = doc(db, 'User', firebaseUser.uid);
    const userDoc = await getDoc(userDocRef);
    const userExists = userDoc.exists();

    if (userExists) {
      const userData = userDoc.data();
      if (userData.status !== 'active') {
        await updateDoc(userDocRef, {
          status: 'active',
        });
      }
      // Lưu role vào localStorage để dùng sau này
      // localStorage.setItem('userRole', userData.role);
      return {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        role: userData.role,
        status: 'active',
      } as API.CurrentUser;
    } else {
      console.log('Create new docs....');
      // Nếu không tìm thấy user, thực hiện insert user vào Collection User trên firestore
      const newUserDocRef = doc(db, 'User', firebaseUser.uid);
      await setDoc(newUserDocRef, {
        firebaseUserId: firebaseUser.uid,
        password: firebaseUser.email!,
        role: Role.member,
        displayName: '',
        status: 'active',
      });

      return {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: '',
        role: Role.member,
        status: 'active',
      } as API.CurrentUser;
    }
  };

  const fetchGame = async (gameId: string) => {
    try {
      // Implement API call để lấy thông tin game
      const response = await getGame(gameId);
      if (response && response.projectId) {
        currentGame = {
          ...response,
          id: gameId,
        };
        // localStorage.setItem(gameId, JSON.stringify(currentGame));
        return currentGame;
      }
      return undefined;
    } catch (error) {
      return undefined;
    }
  };

  const updateGameFunc = async (
    gameId: string,
    data: any,
  ): Promise<{ success: boolean } | undefined> => {
    try {
      const response: { success: boolean } = (await updateGame(gameId, data)) ?? { success: false };
      if (!response) {
        throw new Error('Failed to update game');
      }
      if (response && response.success) {
        const newGame = await fetchGame(gameId);
        // await setInitialState?.((s) => ({
        //   ...s,
        //   currentGame: newGame,
        // }));
      }
      return response;
    } catch (error) {
      return undefined;
    }
  };

  if (gameId) {
    let gameData = localStorage.getItem(gameId);
    if (gameData) {
      localStorage.removeItem(gameId);
    } else {
      // Nếu tìm thấy game data trong localStorage, parse nó
      // currentGame = JSON.parse(gameData);
    }

    gameData = await fetchGame(gameId);
    if (!gameData) {
      // Nếu không tìm thấy game data trong localStorage, redirect về trang GameList
      history.push('/game-list');
      return {
        settings: defaultSettings as Partial<LayoutSettings>,
        currentUser: auth.currentUser ? await fetchUser() : undefined,
        fetchUser,
        fetchGame,
        updateGame: updateGameFunc,
      };
    }
  }

  if (auth.currentUser) {
    const currentUser = await fetchUser();
    return {
      currentUser,
      currentGame,
      fetchUser,
      fetchGame,
      updateGame: updateGameFunc,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    currentGame,
    fetchUser,
    fetchGame,
    updateGame: updateGameFunc,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// const menuLayout1 = [
//   {
//     path: '/',
//     name: 'User',
//    // icon: 'smile',
//     component: './Social/User',
//   },
//   {
//     path: '/games/mail',
//     name: 'Mail System',
//     //icon: 'smile',
//     component: './Social/Mail',
//   },
// ];

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.email
        ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(initialState?.currentUser?.email)}`
        : 'https://api.dicebear.com/7.x/avataaars/svg?seed=default',
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.email,
    //},
    // headerRender: (props, defaultDom) => {
    //   console.log(props, defaultDom);
    //   return defaultDom;
    // },
    pageTitleRender: (props, defaultPageTitle, info) => {
      return `${info?.pageName || ''} - Admin Tool`;
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          // <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          //   <LinkOutlined />
          //   <span>OpenAPI Docs</span>
          // </Link>,
        ]
      : [],
    menuHeaderRender: (logo: React.ReactNode, title: React.ReactNode, props: any) => {
      const location = useLocation();
      const isGameDetail = location.pathname.includes('/games/');
      if (isGameDetail) {
        return <GameMenuHeader collapsed={props?.collapsed} />;
      }
      return <></>;
    },
    menuItemRender: (menuItemProps, defaultDom) => {
      const location = useLocation();
      const gameId = location.pathname.split('/')[2]; // Lấy gameId từ URL hiện tại
      const highlightStyle = { color: PRIMARY_COLOR };

      if (menuItemProps.isUrl || !menuItemProps.path || location.pathname === menuItemProps.path) {
        return (
          <Space style={menuItemProps.path === location.pathname ? highlightStyle : {}}>
            {/* <div >
            </div> */}
            {menuItemProps.pro_layout_parentKeys.length > 0 ? menuItemProps.icon : null}
            {defaultDom}
            {/* {defaultDom} */}
            {/* <div style={menuItemProps.path === location.pathname ? highlightStyle : {}}>{}</span> */}
            {/* {menuItemProps.name} */}
          </Space>
        );
      }

      // Thay thế :gameId trong path với gameId thực
      const path = menuItemProps.path.replace(':gameId', gameId);

      return (
        <div style={path === location.pathname ? highlightStyle : {}}>
          <Link to={path}>
            <Space direction="horizontal">
              {menuItemProps.pro_layout_parentKeys.length > 0 ? menuItemProps.icon : null}
              {defaultDom}
            </Space>
          </Link>
        </div>
      );
    },
    menuDataRender: (menuData) => {
      const isGameDetail = window.location.pathname.includes('/games/');
      const role = initialState?.currentUser?.role;
      const analyticsEnabled = initialState?.currentGame?.settings?.analytics?.bigQueryEnable;

      const filterMenuByRole = (menuItem: MenuDataItem) => {
        // Ẩn Economy menu khi BigQuery/Analytics chưa được bật
        if (menuItem.path?.includes('/economy')) {
          if (!analyticsEnabled) return false;
        }

        if (menuItem.children) {
          menuItem.children = menuItem.children.filter((child) => filterMenuByRole(child));
          if (menuItem.children.length === 0 && menuItem.menuType === 'game') {
            return false;
          }
        }
        const { menuType, roles } = menuItem;
        if (menuType === (isGameDetail ? 'game' : 'base')) {
          if (roles && roles.length > 0) {
            return role ? roles.includes(role) : false;
          }
          return true;
        }
        return false;
      };

      return menuData.filter(filterMenuByRole);
    },
    // 自定义 403 页面
    unAccessible: <ForbiddenPage />,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request configuration, can configure error handling
 * It provides a unified network request and error handling solution based on axios and ahooks useRequest.
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL:
    process.env.NODE_ENV === 'development'
      ? 'https://admintool.lihuhugames.com/api'
      : 'https://admintool.lihuhugames.com/api',
  timeout: 30000,
  ...errorConfig,
};
